import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import FundIcon from 'assets/images/funds/moneypool-2.png'
import Fund2Icon from 'assets/images/funds/moneyPool-1.svg'
import { faPlain, toman } from 'utilities/helper/format'
import { WeekConvertor } from '../info/body/date/utils'
import { PencilIcon } from '../icons'

interface IHeaderProps {
    imageId: number
    avatar: string | null
    name: string
    interval: string | null | undefined
    dayOfDue: number | null | undefined
    shareValueAmount: number
}

/**
 * The rule, not the next date — «ماهانه - ۵ام هر ماه» as Android's settings
 * header puts it. This is the screen where the rule is set, and the next
 * date is already on the fund page.
 */
export const dueRule = (
    interval: string | null | undefined,
    day: number | null | undefined
) => {
    if (!interval || !day) return 'ثبت نشده'
    if (interval === 'wkl') {
        const weekday = WeekConvertor[day]?.trim()
        return weekday ? `هفتگی - هر ${weekday}` : 'ثبت نشده'
    }
    return `ماهانه - ${faPlain.format(day)}ام هر ماه`
}

/**
 * Port of the top of Android's `activity_moneypool_settings.xml`: an 80dp
 * round logo on the right, the name on its top edge, سررسید and حق عضویت on
 * its bottom edge, the pencil on the far left of that last line, and a
 * hairline 24dp below.
 */
export default function SettingHeader({
    imageId,
    avatar,
    name,
    interval,
    dayOfDue,
    shareValueAmount,
}: IHeaderProps) {
    const { id } = useParams()
    const navigate = useNavigate()
    // same rule as the fund page: an uploaded image lost in the bucket
    // migration falls back to the default logo instead of a broken glyph
    const [failedAvatar, setFailedAvatar] = useState<string | null>(null)
    const showCustomAvatar = Boolean(avatar) && avatar !== failedAvatar

    return (
        <section className="bg-white pt-4">
            <div className="flex items-stretch gap-2 px-4">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-[#E2EED9]">
                    {showCustomAvatar ? (
                        <img
                            src={avatar as string}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={() => setFailedAvatar(avatar)}
                        />
                    ) : (
                        <img
                            src={imageId === 2 ? FundIcon : Fund2Icon}
                            alt=""
                            className="h-full w-full object-contain p-3"
                        />
                    )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between">
                    <p className="truncate text-base font-bold text-black">
                        {name}
                    </p>
                    <div className="text-sm">
                        <p className="truncate">
                            <span className="font-bold text-text-muted">
                                تاریخ سررسید:
                            </span>{' '}
                            <span className="text-text-body">
                                {dueRule(interval, dayOfDue)}
                            </span>
                        </p>
                        <div className="mt-1 flex items-center justify-between gap-2">
                            <p className="min-w-0 truncate">
                                <span className="font-bold text-text-muted">
                                    حق عضویت:
                                </span>{' '}
                                <span className="text-text-body">
                                    {shareValueAmount
                                        ? toman(shareValueAmount)
                                        : 'ثبت نشده'}
                                </span>
                            </p>
                            <button
                                type="button"
                                aria-label="ویرایش اطلاعات صندوق"
                                className="-m-2.5 shrink-0 p-2.5 text-text-muted active:scale-90"
                                onClick={() => {
                                    posthog.capture(
                                        'fund_setting_item_clicked',
                                        { fund_id: id, item: 'info' }
                                    )
                                    navigate(`/fund/setting/info/${id}`)
                                }}
                            >
                                <PencilIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-6 h-px bg-[#D4D4D4]" />
        </section>
    )
}
